/* import __COLOCATED_TEMPLATE__ from './posting-create-add-ons.hbs'; */
import Component from '@glimmer/component';
import { action, get, set } from '@ember/object';
import { inject as service } from '@ember/service';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import {
  POSTING_CREATE_COVER_EXPENSES_TYPE,
  POSTING_CREATE_COVER_FEES_TYPE, POSTING_CREATE_EXPEDITE_TYPE,
} from 'sh-www/constants/routes/posting-create';

const ADD_ON_NO_TO_YES_COPY = 'While this change only impacts future applicants, you can still enable this add-on to existing applicants/projects individually.';
const ADD_ON_YES_TO_NO_COPY = 'This change will only impact future applicants.';

const CLASS_NAME = 'SH-c-posting-create-add-ons';

class PostingCreateAddOns extends Component {
  @service('states/routes/posting-create') state;
  @service('state-managers/routes/posting-create') stateManager;
  @service session;

  get assignment() {
    return get(this.state, 'editingPosting');
  }

  get areFormElementsDisabled() {
    return get(this.state, 'areFormElementsDisabled');
  }

  get errors() {
    return get(this.state, 'errors');
  }

  get updatedCoverExpenses() {
    return get(this.state, 'updatedCoverExpenses');
  }

  get updatedCoverFees() {
    return get(this.state, 'updatedCoverFees');
  }

  get updatedExpedite() {
    return get(this.state, 'updatedExpedite');
  }

  get className() {
    return CLASS_NAME;
  }

  get coverExpensesWasChangedCopy() {
    // only show copy if editing the add-on on an existing posting
    if (!this.isEditingPublishedPosting || !this.hasCoverExpensesChanged) {
      return '';
    }

    return this.updatedCoverExpenses === POSTING_CREATE_COVER_EXPENSES_TYPE.YES
      ? ADD_ON_NO_TO_YES_COPY
      : ADD_ON_YES_TO_NO_COPY;
  }

  get coverFeesWasChangedCopy() {
    // only show copy if editing the add-on on an existing posting
    if (!this.isEditingPublishedPosting || !this.hasCoverFeesChanged) {
      return '';
    }

    return this.updatedCoverFees === POSTING_CREATE_COVER_FEES_TYPE.YES
      ? ADD_ON_NO_TO_YES_COPY
      : ADD_ON_YES_TO_NO_COPY;
  }

  get expediteWasChangedCopy() {
    // only show copy if editing the add-on on an existing posting
    if (!this.isEditingPublishedPosting || !this.hasExpediteChanged) {
      return '';
    }

    return this.updatedExpedite === POSTING_CREATE_EXPEDITE_TYPE.YES
      ? ADD_ON_NO_TO_YES_COPY
      : ADD_ON_YES_TO_NO_COPY;
  }

  get expediteUpsellCopy() {
    if (this.session.currentUser?.company) {
      const feePercentage = parseFloat((this.session.currentUser.company.expeditePaymentFee * 100).toFixed(2));
      return `Promote your posting by offering expedited payment for a small fee (${feePercentage}% of final amount).`;
    } else {
      return `Promote your posting by offering expedited payment for a small fee.`;
    }
  }

  get coverExpensesNo() {
    return POSTING_CREATE_COVER_EXPENSES_TYPE.NO;
  }

  get coverExpensesYes() {
    return POSTING_CREATE_COVER_EXPENSES_TYPE.YES;
  }

  get coverFeesNo() {
    return POSTING_CREATE_COVER_FEES_TYPE.NO;
  }

  get coverFeesYes() {
    return POSTING_CREATE_COVER_FEES_TYPE.YES;
  }

  get expediteNo() {
    return POSTING_CREATE_EXPEDITE_TYPE.NO;
  }

  get expediteYes() {
    return POSTING_CREATE_EXPEDITE_TYPE.YES;
  }

  /**
   * Compare assignment state with user updated state to see if cover expenses has changed.
   *
   * @return {boolean}
   */
  get hasCoverExpensesChanged() {
    if (!this.assignment) return false;

    const currentValue = this.updatedCoverExpenses === POSTING_CREATE_COVER_EXPENSES_TYPE.YES
      ? true
      : false;

    return this.assignment.isAllowsExpenses !== currentValue;
  }

  /**
   * Compare assignment state with user updated state to see if cover fees has changed.
   *
   * @return {boolean}
   */
  get hasCoverFeesChanged() {
    if (!this.assignment) return false;

    const currentValue = this.updatedCoverFees === POSTING_CREATE_COVER_FEES_TYPE.YES
      ? true
      : false;

    return this.assignment.sellerFeesCovered !== currentValue;
  }

  /**
   * Return true if expediting value has changed
   * @return {boolean}
   */
  get hasExpediteChanged() {
    if (!this.assignment) return false;

    const currentValue = this.updatedExpedite === POSTING_CREATE_EXPEDITE_TYPE.YES
      ? true
      : false;

    return this.assignment.expediteCommitment !== currentValue;
  }

  get isCoverExpensesNoSelected() {
    return POSTING_CREATE_COVER_EXPENSES_TYPE.NO === this.updatedCoverExpenses;
  }

  get isCoverExpensesYesSelected() {
    return POSTING_CREATE_COVER_EXPENSES_TYPE.YES === this.updatedCoverExpenses;
  }

  get isCoverFeesNoSelected() {
    return POSTING_CREATE_COVER_FEES_TYPE.NO === this.updatedCoverFees;
  }

  get isCoverFeesYesSelected() {
    return POSTING_CREATE_COVER_FEES_TYPE.YES === this.updatedCoverFees;
  }

  get isExpediteNoSelected() {
    return POSTING_CREATE_EXPEDITE_TYPE.NO === this.updatedExpedite;
  }

  get isExpediteYesSelected() {
    return POSTING_CREATE_EXPEDITE_TYPE.YES === this.updatedExpedite;
  }

  get isEditingPublishedPosting() {
    if (!this.assignment) {
      return false;
    }

    return this.assignment.status !== ASSIGNMENT.STATUS.DRAFT;
  }

  /**
   * Called when user selects a cover expenses button
   *
   * @param {String} coverExpensesType    of type POSTING_CREATE_COVER_EXPENSES_TYPE.*
   */
  @action
  onSelectCoverExpenses(coverExpensesType) {
    set(this.state, 'errors', {
      ...this.errors,
      expense: undefined,
    });

    this.stateManager.updateCoverExpenses(coverExpensesType);
  }

  /**
   * Called when user selects a cover expenses button
   *
   * @param {String} coverFeesType    of type POSTING_CREATE_COVER_FEES_TYPE.*
   */
  @action
  onSelectCoverFees(coverFeesType) {
    set(this.state, 'errors', {
      ...this.errors,
      seller_fees_covered: undefined,
    });

    this.stateManager.updateCoverFees(coverFeesType);
  }

  /**
   * Called when user selects an expedite button
   *
   * @param {String} expediteType    of type POSTING_CREATE_EXPEDITE_TYPE.*
   */
  @action
  onSelectExpedite(expediteType) {
    set(this.state, 'errors', {
      ...this.errors,
      expedite: undefined,
    });

    this.stateManager.updateExpedite(expediteType);
  }
}

export default PostingCreateAddOns;
